import Checkbox from "@components/Checkbox";
import PageContent from "@components/PageContent";
import PageHeader from "@components/PageHeader";
import Table from "@components/Table";
import { Helmet } from "react-helmet";
import {
  FaSortDown,
  FaSortUp,
  FaSort,
  FaCheck,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import { queryHelper } from "@utils/queryHelper";
import { useInjectReducer } from "@app/store";
import {
  useDeleteAllMutation,
  useDeleteMultipleErrorRequestMutation,
  useLoadClientErrorMutation,
} from "@features/admin/errorClient/errorClientApiSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  makeSelectClientChosenError,
  makeSelectQuery,
} from "@features/admin/errorClient/errorClientSelector";
import { useEffect, useState } from "react";
import errorClientSlice, {
  clearChosenError,
  setQueryValue,
  addClientChosenError,
} from "@features/admin/errorClient/errorClientSlice";
import moment from "moment";
import { DATE_TIME_FORMAT } from "@utils/constants";
import Dialog from "@components/Dialog";
import Button from "@components/Button";
import { FilterInput } from "@components/FilterInputs";
import { RiSearch2Line } from "react-icons/ri";
import DeleteDialog from "@components/DeleteDialog";
import { enqueueSnackbar } from "@features/auth/authSlice";

const ClientErrors = () => {
  useInjectReducer("errorClient", errorClientSlice);
  const dispatch = useDispatch();

  const query = useSelector(makeSelectQuery);
  const chosen_error = useSelector(makeSelectClientChosenError);

  const [
    allClientErrors,
    { data: loadAll, isLoading: loading, isSuccess: success },
  ] = useLoadClientErrorMutation();

  const [deleteMultipleErrorRequest, { isLoading: deleting }] =
    useDeleteMultipleErrorRequestMutation();

  const [deleteAll, { isLoading: deletingAll }] = useDeleteAllMutation();

  useEffect(() => {
    dispatch(clearChosenError());
    allClientErrors(queryHelper(query));
  }, []);

  const [show, setShow] = useState(false);
  const [stack, setStack] = useState("");
  const [open, setOpen] = useState(false);
  const [openMultiple, setMultipleOpen] = useState(false);
  const [deletedId, setDeletedId] = useState("");

  const handleQueryChange = (name) => (e) => {
    dispatch(setQueryValue({ key: name, value: e.target.value }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSearch = () => {
    allClientErrors(queryHelper(query));
  };

  const handleOpenAll = () => {
    setOpen(true);
  };

  const handlePagination = ({ page, size }) => {
    dispatch(setQueryValue({ key: "page", value: page }));
    dispatch(setQueryValue({ key: "size", value: size }));
  };

  const handleShow = (id, stack) => {
    setStack(stack);
    setShow(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShow(false);
  };

  const handleDelete = () => {
    setOpen(false);
  };

  const handleOpenForMultiple = (id) => {
    setMultipleOpen(true);
    setDeletedId(id);
  };

  const handleDeleteForMultiple = (id) => {
    const data = {
      bug_id: id,
    };
    deleteMultipleErrorRequest(data)
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "Error deleted successfully.",
              options: {
                variant: "success",
              },
            })
          );
          setMultipleOpen(false);
          allClientErrors(queryHelper(query));
          dispatch(clearChosenError(id));
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Error Delete Failed.",
            options: {
              variant: "warning",
            },
          })
        );
      });
  };

  const handleDeleteAll = () => {
    deleteAll()
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg ?? "All errors deleted successfully.",
              options: {
                variant: "success",
              },
            })
          );
          allClientErrors(queryHelper(query));
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg ?? "Error Delete Failed.",
            options: {
              variant: "warning",
            },
          })
        );
      });
    setOpen(false);
  };

  const handleCloseForMultiple = () => {
    setMultipleOpen(false);
  };

  const tablePagination = {
    page: loadAll?.page ?? 1,
    size: loadAll?.size ?? 10,
    totalData: loadAll?.totalData ?? 0,
  };

  const sort = {};
  const tableData = success
    ? loadAll.data.map(
        ({
          error_data,
          added_at,
          count,
          last_added_at,
          location,
          _id,
          mode,
          error_stack,
        }) => [
          <Checkbox
            name={_id}
            checked={chosen_error.includes(_id)}
            onChange={() => dispatch(addClientChosenError(_id))}
          />,
          <div>
            {error_data.substring(0, 200)}
            <div className='flex gap-2 items-center'>
              <div> Location: </div>
              <div className='text-xs text-blue-400'>{location}</div>
              <div>|</div>
              <a
                href={location}
                target='_blank'
                className='text-xs text-blue-400 hover:text-primary'
              >
                Go To
              </a>
              <div>|</div>
              <button
                className='texbt-xs text-blue-400 hover:text-primary'
                onClick={() =>
                  handleShow(
                    _id,
                    error_stack ? error_stack : "Error Stack Not Found."
                  )
                }
              >
                View Error Stack
              </button>
            </div>
          </div>,
          <div className='capitalize'>{mode}</div>,
          count,
          moment(added_at).format(DATE_TIME_FORMAT),
          last_added_at != null
            ? moment(last_added_at).format(DATE_TIME_FORMAT)
            : moment(added_at).format(DATE_TIME_FORMAT),
        ]
      )
    : [];

  return (
    <>
      <DeleteDialog
        open={open}
        doClose={handleClose}
        doDelete={() =>
          deletedId && deletedId != ""
            ? handleDelete(deleteId)
            : handleDeleteAll()
        }
      />

      <DeleteDialog
        open={openMultiple}
        doClose={handleCloseForMultiple}
        doDelete={() => handleDeleteForMultiple(chosen_error)}
      />
      <Dialog
        opened={show}
        size='70%'
        onClose={handleClose}
        title={"Error Stack"}
        actions={
          <button
            type='button'
            className='block btn margin-none text-white bg-red-500 border border-red-600 hover:bg-red-600'
            onClick={handleClose}
          >
            Close
          </button>
        }
      >
        <p> {stack} </p>
      </Dialog>
      <Helmet>
        <title> Client Error Listing </title>
      </Helmet>
      <div className='flex justify-between my-3'>
        <div className='flex flex-col'>
          <PageHeader title='Client Error Manage' />
          <div className='text-sm text-blue-500'>
            **Please delete errors if fixed. Thank you.
          </div>
        </div>
        <Button onClick={handleOpenAll}>Delete All</Button>
      </div>
      <PageContent loading={loading}>
        <div className='flex justify-between items-center'>
          <div>
            {chosen_error.length > 0 ? (
              <div className='flex items-center'>
                <Button
                  className='mx-4'
                  onClick={() => handleOpenForMultiple(chosen_error)}
                >
                  <div className='flex items-center'>
                    <FaTrash />
                    <span className='pl-2'> Delete </span>
                  </div>
                </Button>
              </div>
            ) : null}
          </div>
          <div className='flex mb-2 gap-2 px-4'>
            <FilterInput
              label='Search Errors'
              fieldsetId='name'
              value={query.find_error_data ?? ""}
              onChange={handleQueryChange("find_error_data")}
              // onKeyDown={handleKeyDown}
            />

            <Button
              leftSection={<RiSearch2Line />}
              isFilter
              onClick={handleSearch}
            >
              Search
            </Button>
          </div>
        </div>

        <Table
          tableHead={[
            <Checkbox name='checkbox' />,
            "Error Message",
            "Environment Mode",
            <span
              className='cursor-pointer flex items-center justify-between'
              onClick={() => handleSortChange("count")}
            >
              Count
              {sort && sort.count ? (
                sort.count == -1 ? (
                  <FaSortUp />
                ) : (
                  <FaSortDown />
                )
              ) : (
                <FaSort />
              )}
            </span>,
            <span
              className='cursor-pointer flex items-center justify-between'
              onClick={() => handleSortChange("added_at")}
            >
              First Encountered At
              {sort && sort.added_at ? (
                sort.added_at == -1 ? (
                  <FaSortUp />
                ) : (
                  <FaSortDown />
                )
              ) : (
                <FaSort />
              )}
            </span>,
            <span
              className='cursor-pointer flex items-center justify-between '
              onClick={() => handleSortChange("last_added_at")}
            >
              Last Encountered At
              {sort && sort.last_added_at ? (
                sort.last_added_at == -1 ? (
                  <FaSortUp />
                ) : (
                  <FaSortDown />
                )
              ) : (
                <FaSort />
              )}
            </span>,
          ]}
          tableData={tableData}
          pagination={tablePagination}
          handlePagination={handlePagination}
          emptyDataMsg='No Error Found'
          loading={loading}
          ispublic
        />
      </PageContent>
    </>
  );
};

export default ClientErrors;
